import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "../utils/analytics";

const handleOverlayCtaClick = (variant, onClose) => {
  trackEvent('overlay_cta_click', {
    cta_name: 'newcastle_herald_read_more',
    cta_location: 'home_overlay',
    cta_variant: variant,
  });
  onClose();
};

export default function HomeOverlay({ onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    // Set a timeout to display the overlay after page load
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1500); // Show overlay after 1.5 seconds

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isVisible) {
      // Sequence of animations
      const stageTimers = [
        setTimeout(() => setAnimationStage(1), 300),  // First content appears
        setTimeout(() => setAnimationStage(2), 600),  // Second content appears
      ];

      return () => stageTimers.forEach(timer => clearTimeout(timer));
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      className="absolute left-1/2 -ml-[148px] min-[900px]:left-[55%] min-[900px]:ml-0 xl:left-[5%] top-[240px] min-[900px]:top-[50px] xl:top-[440px] z-5"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
      }}
    >
      {/* Desktop and Tablet Layout (in landscape orientation) */}
      <div
        className="hidden min-[900px]:flex min-[900px]:flex-col w-[329px] min-[900px]:w-[329px] xl:w-[548px] rounded-[25px] border-[3px] border-[#910000] bg-[rgba(3,28,31,0.78)] p-5 xl:p-8 backdrop-blur-sm overflow-hidden relative"
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#03181F]/30 to-transparent"></div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 xl:top-4 xl:right-4 text-white hover:text-[#C3EEFD] transition-colors z-20"
          aria-label="Close overlay"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 xl:w-6 xl:h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Main content - full width */}
        <div
          className="relative z-10 text-[1.8rem] min-[900px]:text-[2.2rem] xl:text-[2.6rem] leading-[2.2rem] min-[900px]:leading-[2.6rem] xl:leading-[3.3rem] mb-1 transition-all duration-500 pt-3 xl:pt-6 pr-6"
          style={{
            opacity: animationStage >= 1 ? 1 : 0,
            transform: animationStage >= 1 ? 'translateY(0)' : 'translateY(10px)',
          }}
        >
          NSW Premier Chris Minns visits John Hunter Hospital to celebrate landmark stroke trial results
        </div>

        {/* Bottom row: CTA on left, Herald logo on right */}
        <div
          className="relative z-10 flex justify-between items-end mt-4 transition-all duration-500"
          style={{
            opacity: animationStage >= 2 ? 1 : 0,
            transform: animationStage >= 2 ? 'translateY(0)' : 'translateY(10px)',
          }}
        >
          <Link
            to="/media/nbnnews"
            onClick={() => handleOverlayCtaClick('desktop_button', onClose)}
            className="button bg-secondary-navy hover:!text-primary !no-underline !text-white normal-case text-[1.6rem] xl:text-[1.6rem] px-4 xl:px-6 py-2 min-[900px]:py-1 xl:py-3"
          >
            Read more
          </Link>

          <div className="w-[130px] xl:w-[170px] shrink-0">
            <img
              src="/nbn-news-logo.png"
              alt="NBN News Logo"
              className="w-full h-auto object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://www.newcastleherald.com.au/static/1777000829/sites/newcastle-herald/images/masthead/masthead-underline.svg";
              }}
            />
          </div>
        </div>
      </div>

      {/* Mobile Layout (in portrait orientation with same aspect ratio but axes swapped) */}
      <div
        className="min-[900px]:hidden w-[296px] h-[375px] rounded-[25px] border-[3px] border-[#910000] bg-[rgba(3,28,31,0.78)] p-6 backdrop-blur-sm overflow-hidden relative flex flex-col"
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#03181F]/30 to-transparent"></div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-[#C3EEFD] transition-colors z-20"
          aria-label="Close overlay"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col flex-grow-0 justify-between">
          {/* Main content */}
          <div
            className="text-[2.6rem] leading-[3.2rem] transition-all duration-500 pt-4"
            style={{
              opacity: animationStage >= 1 ? 1 : 0,
              transform: animationStage >= 1 ? 'translateY(0)' : 'translateY(10px)',
            }}
          >
            NSW Premier Chris Minns visits John Hunter Hospital to celebrate landmark stroke trial results
          </div>

          {/* CTA Buttons */}
          <div
            className="flex transition-all duration-500 pt-2"
            style={{
              opacity: animationStage >= 2 ? 1 : 0,
              transform: animationStage >= 2 ? 'translateY(0)' : 'translateY(10px)',
            }}
          >
            <Link
              to="/media/nbnnews"
              onClick={() => handleOverlayCtaClick('mobile_text', onClose)}
              className="button font-bold bg-transparent hover:!text-primary !no-underline !text-white normal-case text-[2rem] px-1 pt-1 pb-0"
            >
              Read More
            </Link>
          </div>

          {/* Nature Journal Image - Top */}
          <div className="relative z-10 flex items-center justify-center w-full h-[65px] my-6">
            <Link
              to="/media/nbnnews"
              onClick={() => handleOverlayCtaClick('mobile_logo', onClose)}
              className="button font-bold bg-transparent hover:!text-primary !no-underline !text-white normal-case text-[1.8rem] px-1 pt-0 pb-0"
            >
              <img
                src="/nbn-news-logo.png"
                alt="NBN News Logo"
                className="max-h-[80%] max-w-[80%] pt-10 object-contain rounded-lg"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://www.newcastleherald.com.au/static/1777000829/sites/newcastle-herald/images/masthead/masthead-underline.svg";
                }}
              />
            </Link>
          </div>

        </div>
      </div>
    </div>

  );
}
